const holidays = new Set([
  '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25', '2026-08-31', '2026-12-25', '2026-12-28',
  '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28',
  '2028-01-03', '2028-04-14', '2028-04-17', '2028-05-01', '2028-05-29', '2028-08-28', '2028-12-25', '2028-12-26',
]);
const londonClock = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});
const dayKey = date => date.toISOString().slice(0, 10);
const nextDay = date => new Date(date.getTime() + 86400000);
const covered = date => date.getUTCFullYear() >= 2026 && date.getUTCFullYear() <= 2028;
const publicHoliday = date => holidays.has(dayKey(date)) || ['01-01', '12-25', '12-26'].includes(dayKey(date).slice(5));
const workingDay = date => date.getUTCDay() !== 0 && date.getUTCDay() !== 6 && !publicHoliday(date);
const deliveryDay = date => date.getUTCDay() !== 0 && !publicHoliday(date);

export function dispatchEstimate(now = new Date(), blackoutDates = []) {
  if (!Number.isFinite(now.getTime())) return null;
  const parts = Object.fromEntries(londonClock.formatToParts(now).map(part => [part.type, part.value]));
  const today = new Date(`${parts.year}-${parts.month}-${parts.day}T00:00:00Z`);
  if (!covered(today)) return null;
  const closures = new Set(blackoutDates.map(date => date.trim()));
  const secondsUntilNoon = 43200 - (Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second));
  const sameDayDispatch = workingDay(today) && !closures.has(dayKey(today)) && secondsUntilNoon > 0;
  let dispatch = sameDayDispatch ? today : nextDay(today);
  for (let attempts = 0; attempts < 370; attempts++, dispatch = nextDay(dispatch)) {
    if (!covered(dispatch)) return null;
    if (workingDay(dispatch) && !closures.has(dayKey(dispatch))) break;
    if (attempts === 369) return null;
  }
  return {
    dispatchDate: dayKey(dispatch),
    daysUntilDispatch: (dispatch.getTime() - today.getTime()) / 86400000,
    secondsRemaining: sameDayDispatch ? secondsUntilNoon : null,
  };
}

export function deliveryEstimate(now = new Date(), blackoutDates = []) {
  const estimate = dispatchEstimate(now, blackoutDates);
  if (!estimate) return null;
  const dispatch = new Date(`${estimate.dispatchDate}T00:00:00Z`);
  let delivery = nextDay(dispatch);
  while (covered(delivery) && !deliveryDay(delivery)) delivery = nextDay(delivery);
  if (!covered(delivery)) return null;
  return {
    deliveryDate: dayKey(delivery),
    daysUntilDelivery: estimate.daysUntilDispatch + (delivery.getTime() - dispatch.getTime()) / 86400000,
    secondsRemaining: estimate.secondsRemaining,
  };
}

export function deliveryMessageParts(estimate, labels, locale = 'en-GB', mode = 'delivery') {
  const daysUntil = mode === 'dispatch' ? estimate.daysUntilDispatch : estimate.daysUntilDelivery;
  const dateKey = mode === 'dispatch' ? estimate.dispatchDate : estimate.deliveryDate;
  const date = daysUntil === 1 ? labels.tomorrow : new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC', weekday: 'long',
    ...(daysUntil >= 7 ? { day: 'numeric', month: 'short' } : {}),
  }).format(new Date(`${dateKey}T00:00:00Z`));
  let template = labels.orderNow;
  if (estimate.secondsRemaining !== null) {
    const hours = Math.floor(estimate.secondsRemaining / 3600);
    const minutes = Math.floor(estimate.secondsRemaining / 60) % 60;
    const seconds = estimate.secondsRemaining % 60;
    const countdown = (hours ? labels.hours : labels.minutes)
      .replace('[hours]', hours)
      .replace('[minutes]', hours ? String(minutes).padStart(2, '0') : minutes)
      .replace('[seconds]', String(seconds).padStart(2, '0'));
    template = labels.orderWithin.replace('[countdown]', countdown);
  }
  if (!template.includes('[date]')) return [template, '', ''];
  const [prefix, suffix = ''] = template.split('[date]');
  const displayDate = suffix.startsWith('.') && date.endsWith('.') ? date.slice(0, -1) : date;
  return [prefix, displayDate, suffix];
}

export function deliveryMessage(estimate, labels, locale = 'en-GB') {
  return deliveryMessageParts(estimate, labels, locale).join('');
}

export function dispatchMessage(estimate, labels, locale = 'en-GB') {
  return deliveryMessageParts(estimate, labels, locale, 'dispatch').join('');
}

if (typeof window !== 'undefined' && !customElements.get('nackt-delivery-countdown')) {
  customElements.define('nackt-delivery-countdown', class extends HTMLElement {
    static observedAttributes = ['data-variant-id', 'data-available'];

    connectedCallback() {
      try {
        this.config = JSON.parse(this.querySelector('[data-delivery-config]').textContent);
        this.message = this.querySelector('[data-delivery-message]');
        this.messagePrefix = document.createTextNode('');
        this.deliveryDate = document.createElement('strong');
        this.deliveryDate.className = 'nackt-delivery-countdown__date';
        this.messageSuffix = document.createTextNode('');
        this.message.replaceChildren(this.messagePrefix, this.deliveryDate, this.messageSuffix);
        this.onVisibility = () => this.refresh();
        document.addEventListener('visibilitychange', this.onVisibility);
        this.refresh();
      } catch {
        this.hidden = true;
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    attributeChangedCallback() {
      if (this.isConnected && this.config) this.refresh();
    }

    refresh() {
      clearTimeout(this.timer);
      const eligible = this.dataset.available === 'true'
        && this.config.eligibleVariants.includes(Number(this.dataset.variantId));
      const now = new Date();
      const estimateForMarket = this.config.mode === 'dispatch' ? dispatchEstimate : deliveryEstimate;
      const estimate = eligible ? estimateForMarket(now, this.config.blackoutDates) : null;
      this.hidden = !estimate;
      if (!estimate) return;
      const [prefix, date, suffix] = deliveryMessageParts(estimate, this.config.labels, document.documentElement.lang || 'en-GB', this.config.mode);
      if (this.messagePrefix.data !== prefix) this.messagePrefix.data = prefix;
      if (this.deliveryDate.textContent !== date) this.deliveryDate.textContent = date;
      if (this.messageSuffix.data !== suffix) this.messageSuffix.data = suffix;
      const tick = estimate.secondsRemaining === null ? 60000 : 1000;
      if (!document.hidden) this.timer = setTimeout(() => this.refresh(), tick - now.getTime() % tick + 20);
    }
  });
}
